/**
 * 🎓 ARFirstUseTour — friendly onboarding for the Interactive Gym/Kitchen.
 *
 * Behaviour:
 *   • First time a user visits an AR shell, an in-app tour panel drops
 *     down with 5 numbered cards. Skip / next / done buttons. Never
 *     shown again once dismissed (per-space localStorage flag).
 *   • A permanent **? Help** pill in the top-right of the AR shell
 *     re-opens the same panel any time.
 *
 * Cards match the FAQ v4 language so users see consistent messaging
 * whether they hit the tour, the ? button, or the /faq page.
 */
import { useCallback, useEffect, useState } from "react";
import { HelpCircle, ChevronLeft, ChevronRight, X, Camera, Image as ImageIcon, Pin, GraduationCap, Sparkles } from "lucide-react";

const STORAGE_KEY = (space) => `omni_ar_tour_seen_${space}`;

// One card per major concept. Same wording as FAQ v4 for consistency.
const buildCards = (spaceLabel) => [
  {
    icon:  Camera,
    color: "text-emerald-300",
    title: `Welcome to your Interactive ${spaceLabel}`,
    body: (
      <>
        The camera turns your phone into your fitness / wellness partner.
        Point it at your real {spaceLabel}, then swipe up the emerald handle
        at the bottom to open all the controls: your loadout, room gallery,
        and the AI session builder.
      </>
    ),
  },
  {
    icon:  Pin,
    color: "text-emerald-200",
    title: "📌 Anchors",
    body: (
      <>
        Tap <b>Anchors</b> top-right to arm placement mode, then tap any
        real object in the view (dumbbells, stove, water bottle) to drop a
        labeled pin. Pins stick to their spot, are draggable, and save to
        your device automatically.
      </>
    ),
  },
  {
    icon:  Camera,
    color: "text-emerald-300",
    title: "🟢 ID Gear vs 🔵 + Room",
    body: (
      <>
        <b>ID Gear</b> identifies a <i>single</i> piece of gear (one
        dumbbell, one skillet) and adds it to your <b>Loadout</b>.
        <br />
        <b>+ Room</b> takes a wider snap and adds it to your <b>Room
        Gallery</b> — over time your {spaceLabel} builds itself from photos.
      </>
    ),
  },
  {
    icon:  Sparkles,
    color: "text-amber-300",
    title: '"Work with what you have" AI Coach',
    body: (
      <>
        Once you&apos;ve scanned a few pieces of gear, tap <b>💪 Workout with
        this</b> or <b>🍳 Cook with this</b>, pick your difficulty
        (🌱 Newbie · 🔥 Intermediate · ⚡ Advanced), and hit
        <b> Build my session</b>. The coach only uses gear you actually own.
      </>
    ),
  },
  {
    icon:  GraduationCap,
    color: "text-emerald-300",
    title: "▶ Play with Avatar Coach",
    body: (
      <>
        Your custom 2D avatar becomes your teacher. It mimics every move
        or cooking step in a fullscreen guided flow with voice narration.
        Prev / Next through steps at your pace, or auto-advance for a
        hands-free session.
      </>
    ),
  },
];

/**
 * Props
 *   space: "gym" | "kitchen"
 *   liveOnly: if true, only render when the camera is live
 */
export function ARFirstUseTour({ space, liveOnly = true, cameraLive }) {
  const spaceLabel = space === "kitchen" ? "kitchen" : "gym";
  const cards = buildCards(spaceLabel);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  // Auto-open the very first time the camera goes live in this space
  useEffect(() => {
    if (liveOnly && !cameraLive) return;
    try {
      const seen = localStorage.getItem(STORAGE_KEY(space));
      if (!seen) {
        setOpen(true);
        setIdx(0);
      }
    } catch { /* ignore */ }
  }, [space, liveOnly, cameraLive]);

  const close = useCallback((markSeen = true) => {
    setOpen(false);
    if (markSeen) {
      try { localStorage.setItem(STORAGE_KEY(space), new Date().toISOString()); } catch { /* ignore */ }
    }
  }, [space]);

  const cur = cards[idx];
  const Icon = cur?.icon || HelpCircle;

  return (
    <>
      {/* Persistent ? Help button — always visible, re-opens the tour */}
      <button
        type="button"
        onClick={() => { setIdx(0); setOpen(true); }}
        className="pointer-events-auto absolute top-24 left-3 z-[62] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 border border-white/30 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-black/90 backdrop-blur"
        data-testid={`ar-help-btn-${space}`}
        title="How does this work?"
        aria-label="Show help"
      >
        <HelpCircle size={12} className="text-emerald-300" />
        Help
      </button>

      {open && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid={`ar-tour-${space}`}>
          <div className="relative max-w-sm w-full bg-neutral-950 border border-emerald-400/40 rounded-2xl shadow-2xl overflow-hidden">
            {/* Progress dots */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {cards.map((_, i) => (
                <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-emerald-400" : "bg-white/20"}`} />
              ))}
            </div>
            {/* Close button */}
            <button
              type="button"
              onClick={() => close(true)}
              className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-white/10"
              data-testid={`ar-tour-close-${space}`}
              aria-label="Close tour"
            >
              <X size={16} className="text-white/70" />
            </button>

            <div className="pt-10 px-5 pb-4">
              <div className={`w-14 h-14 rounded-full bg-black/50 border border-emerald-400/40 flex items-center justify-center mb-3 ${cur?.color || ""}`}>
                <Icon size={26} />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-1">
                Step {idx + 1} of {cards.length}
              </div>
              <div className="text-lg font-black text-white leading-tight mb-2">{cur.title}</div>
              <div className="text-sm text-white/80 leading-relaxed">{cur.body}</div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-white/10 bg-black/30">
              <button
                type="button"
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                disabled={idx === 0}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/15 text-white/80 text-xs font-bold disabled:opacity-30"
                data-testid={`ar-tour-prev-${space}`}
              >
                <ChevronLeft size={12} /> Back
              </button>
              {idx < cards.length - 1 ? (
                <button
                  type="button"
                  onClick={() => setIdx((i) => Math.min(cards.length - 1, i + 1))}
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg bg-emerald-500 text-black text-xs font-black uppercase tracking-widest hover:bg-emerald-400"
                  data-testid={`ar-tour-next-${space}`}
                >
                  Next <ChevronRight size={12} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => close(true)}
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg bg-emerald-500 text-black text-xs font-black uppercase tracking-widest hover:bg-emerald-400"
                  data-testid={`ar-tour-done-${space}`}
                >
                  Let&apos;s go
                </button>
              )}
            </div>

            {/* Footer — link to full FAQ */}
            <div className="px-5 pb-3 text-center">
              <a
                href="/faq"
                className="text-[10px] text-white/40 hover:text-white/70 underline"
                data-testid={`ar-tour-faq-link-${space}`}
              >
                Read the full FAQ →
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ARFirstUseTour;
